// LayerOpacitySlider — レイヤー行の常設「うすさ」スライダ
// (docs/_wip/layer-display-opacity-2026-10-12.md)。Classic / Modern 共通。
//
// - **つまみを掴んでいる間だけ動く**。トラックの空白タップでは値は変わらない
//   (子どもの誤操作・縦スクロールとの競合対策)。touch-action: none はつまみだけ。
// - つまみの当たり判定は 44px 角 (見た目の丸は 16px)。端で隣のボタンに被らないよう、
//   当たり判定ぶん (22px) 左右をインセットして動かす。
// - ドラッグ中だけ、つまみの上に「50%」の吹き出し。普段は数字を出さない。
// - 95% 以上で離したら 100% にスナップ。pointercancel / Esc は元の値へ戻す (end(false))。
// - キーボード ←→ ↑↓ で 5% 刻み (Home = 10%、End = 100%)。

import { useRef, useState } from 'react';

import {
  clampLayerOpacityValue,
  LAYER_OPACITY_MAX,
  LAYER_OPACITY_MIN,
  type LayerOpacityControl,
  layerOpacityPercent,
  snapLayerOpacity,
  stepLayerOpacity,
} from './layerOpacity.js';

const HIT_PX = 44;
const RANGE = LAYER_OPACITY_MAX - LAYER_OPACITY_MIN;

/** つまみのドラッグ終了時刻。直後に飛ぶ click (タッチのゴースト click 等) を行選択に使わない。 */
let lastDragEndAt = Number.NEGATIVE_INFINITY;
const GHOST_CLICK_GUARD_MS = 500;

/**
 * 行 (li) の onClick で「このクリックでレイヤーを選択してよいか」。
 * つまみ・ボタン (👁 / 選択ボタン) 由来は対象外、つまみのドラッグ直後の click も対象外。
 * 溝・余白・番号バッジなど、それ以外はすべて選択。
 */
export const shouldSelectOnRowClick = (e: React.MouseEvent): boolean => {
  const t = e.target;
  if (t instanceof Element && t.closest('[role="slider"], button') !== null) return false;
  return Date.now() - lastDragEndAt > GHOST_CLICK_GUARD_MS;
};

export interface LayerOpacitySliderProps {
  readonly layerId: number;
  /** 例: 「レイヤー2」。aria-label「レイヤー2 のうすさ (自分の画面だけ)」に使う。 */
  readonly label: string;
  /** 現在の濃さ (0.1〜1)。 */
  readonly opacity: number;
  readonly control: LayerOpacityControl | null;
  /** 👁 で非表示の層: 操作はできるが見た目を薄くする。 */
  readonly dimmedLook?: boolean;
  readonly testId?: string;
}

interface DragState {
  readonly startX: number;
  readonly startValue: number;
  readonly pointerId: number;
  last: number;
}

export const LayerOpacitySlider = ({
  layerId,
  label,
  opacity,
  control,
  dimmedLook = false,
  testId,
}: LayerOpacitySliderProps): React.ReactNode => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const [dragValue, setDragValue] = useState<number | null>(null);

  const shown = dragValue ?? clampLayerOpacityValue(opacity);
  const fraction = (shown - LAYER_OPACITY_MIN) / RANGE;
  const percent = layerOpacityPercent(shown);
  const dragging = dragValue !== null;

  const finish = (commit: boolean): void => {
    const d = dragRef.current;
    if (d === null) return;
    lastDragEndAt = Date.now();
    dragRef.current = null;
    setDragValue(null);
    if (control === null) return;
    if (commit) {
      const snapped = snapLayerOpacity(d.last);
      if (snapped !== d.last) control.update(snapped);
      control.end(true);
    } else {
      control.end(false);
    }
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (control === null) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const start = clampLayerOpacityValue(opacity);
    dragRef.current = { startX: e.clientX, startValue: start, pointerId: e.pointerId, last: start };
    setDragValue(start);
    control.begin(layerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const d = dragRef.current;
    if (d === null || control === null || e.pointerId !== d.pointerId) return;
    const width = trackRef.current?.getBoundingClientRect().width ?? 0;
    const travel = Math.max(1, width - HIT_PX);
    const v = clampLayerOpacityValue(d.startValue + ((e.clientX - d.startX) / travel) * RANGE);
    d.last = v;
    setDragValue(v);
    control.update(v);
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>): void => {
    const d = dragRef.current;
    if (d === null || e.pointerId !== d.pointerId) return;
    finish(true);
  };

  const onPointerCancel = (e: React.PointerEvent<HTMLDivElement>): void => {
    const d = dragRef.current;
    if (d === null || e.pointerId !== d.pointerId) return;
    finish(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape') {
      if (dragRef.current !== null) {
        e.preventDefault();
        finish(false);
      }
      return;
    }
    if (control === null || dragRef.current !== null) return;
    const current = clampLayerOpacityValue(opacity);
    let next: number | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = stepLayerOpacity(current, 1);
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = stepLayerOpacity(current, -1);
    else if (e.key === 'Home') next = LAYER_OPACITY_MIN;
    else if (e.key === 'End') next = LAYER_OPACITY_MAX;
    if (next === null) return;
    e.preventDefault();
    control.set(layerId, next);
  };

  return (
    <div
      ref={trackRef}
      data-testid={testId}
      className={`relative h-11 min-w-0 flex-1 ${dimmedLook ? 'opacity-50' : ''}`}
    >
      {/* トラックの線 (飾り。ここは押しても何も起きない)。 */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 h-1 -translate-y-1/2 rounded bg-[var(--color-border)]"
        style={{ left: HIT_PX / 2, right: HIT_PX / 2 }}
      />
      <div
        role="slider"
        tabIndex={0}
        aria-label={`${label} のうすさ (自分の画面だけ)`}
        aria-valuemin={layerOpacityPercent(LAYER_OPACITY_MIN)}
        aria-valuemax={layerOpacityPercent(LAYER_OPACITY_MAX)}
        aria-valuenow={percent}
        aria-valuetext={`${percent}%`}
        aria-orientation="horizontal"
        data-testid={testId === undefined ? undefined : `${testId}-thumb`}
        className="absolute top-0 flex h-11 w-11 -translate-x-1/2 cursor-grab items-center justify-center rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-text)]"
        style={{
          left: `calc(${HIT_PX / 2}px + (100% - ${HIT_PX}px) * ${fraction})`,
          touchAction: 'none',
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onKeyDown={onKeyDown}
        onClick={(e) => e.stopPropagation()}
      >
        <span
          aria-hidden="true"
          className="pointer-events-none block h-4 w-4 rounded-full border-2 border-black bg-white"
        />
        {dragging && (
          <span
            aria-hidden="true"
            data-testid="layer-opacity-bubble"
            className="pointer-events-none absolute bottom-full left-1/2 z-50 -translate-x-1/2 whitespace-nowrap rounded bg-black px-1.5 py-0.5 text-[11px] font-bold leading-none text-white"
          >
            {percent}%
          </span>
        )}
      </div>
    </div>
  );
};
